import React, { useState, useCallback } from 'react';
import { IconCheck, IconStar, IconShield, IconPlus } from '../components/Icons';
import { API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { AI_MONTHLY_QUESTIONS, PRO_PARTNER_PRICE, PRO_PRICE, PRO_TRIAL_DAYS, proPriceFor } from '../utils/proPricing';

/**
 * risklo.io/pricing. Each card lists what the plan includes by area (the way
 * traders compare), then a full table below. Keep in step with the feature gates
 * (server/services/featureAccessService.js) and the AI tier limits.
 */

// [feature, Basic, Pro, Companies, onCard]. A cell: true (included), false (not
// included) or a short text. onCard rows also show on the plan cards; every row
// shows in the comparison table.
const AREAS = [
  {
    title: 'Accounts',
    rows: [
      ['Connected broker accounts', false, 'Up to 5', 'Up to 5 per member', true],
      ['NinjaTrader / Tradovate, Schwab, Robinhood, Webull, IBKR, Coinbase, Alpaca and more', false, true, true],
      ['Read-only connections (RiskLo never places trades)', true, true, true, true],
      ['Broker CSV import', false, true, true],
    ],
  },
  {
    title: 'RiskLo AI',
    isNew: true,
    rows: [
      ['Questions each month', false, `${AI_MONTHLY_QUESTIONS.pro}`, `${AI_MONTHLY_QUESTIONS.max} per staff member`, true],
      ['Explains your own accounts, trades and results', false, true, true, true],
      ['Market calendar, earnings and news, prop firm rules', false, true, true, true],
      ['Deep analysis mode', false, true, true],
    ],
  },
  {
    title: 'Prop firm risk tools',
    rows: [
      ['Account Blowout Risk (GO / NO GO)', 'Preview', true, true, true],
      ['% Rule (consistency) Risk with prop firm presets', 'Preview', true, true, true],
      ['Hedging Risk', 'One pair', true, true],
      ['Bulk Risk: up to 40 setups at once', false, true, true, true],
      ['NinjaTrader CSV upload with auto-mapping', false, true, true],
    ],
  },
  {
    title: 'Portfolio',
    rows: [
      ['My Portfolio: balances, equity curve, trades, positions', false, true, true, true],
      ['Portfolio Simulator: combine strategies and accounts', false, true, true, true],
      ['Position Sizer', true, true, true],
      ['Concentration, Drawdown & VaR, Stop Planner, Rebalancing from your accounts', false, true, true],
      ['Custom dashboards', false, 'Up to 5', 'Up to 5', true],
    ],
  },
  {
    title: 'Strategies & results',
    rows: [
      ['Your brand’s strategy results (calendar, chart, weekly, spreadsheet)', true, true, true, true],
      ['Detailed KPI metrics', false, true, true],
      ['NQ ↔ MNQ contract scaling', true, true, true],
      ['My strategies: your own, from a CSV or a webhook', false, true, true],
      ['Publish strategies to your members', false, false, true, true],
    ],
  },
  {
    title: 'Reports & alerts',
    rows: [
      ['Email risk summaries', false, true, true, true],
      ['Trade Watcher: naked-position alerts on live NinjaTrader accounts', false, false, true, true],
    ],
  },
];

const PLANS = [
  {
    id: 'basic',
    name: 'Basic',
    who: 'For traders exploring strategy results',
    cta: 'Start free',
    note: 'No credit card',
  },
  {
    id: 'pro',
    name: 'Pro',
    who: 'For active traders managing real accounts',
    featured: true,
    cta: `Start ${PRO_TRIAL_DAYS}-day free trial`,
  },
  {
    id: 'company',
    name: 'Companies',
    who: 'For strategy providers, signal services and trading communities',
    price: '$149',
    pricePrefix: 'From',
    period: '/month',
    cta: 'See company plans',
    note: 'Your brand, your clients, your strategies',
  },
];

// Which column a plan reads in AREAS rows.
const COLUMN = { basic: 1, pro: 2, company: 3 };

const PRICING_FAQ = [
  {
    q: 'Is there a free plan?',
    a: 'Yes. Basic is free forever: your brand’s strategy results, contract scaling, guides, and previews of the risk tools. Connected accounts, RiskLo AI, the full risk tools and the Portfolio Simulator are Pro.',
  },
  {
    q: `Why is Pro $${PRO_PARTNER_PRICE} for some people?`,
    a: `Pro is $${PRO_PRICE}/month on your own. Members of a partner company that runs on RiskLo (for example Vector) get Pro for $${PRO_PARTNER_PRICE}/month through that company.`,
  },
  {
    q: `How does the ${PRO_TRIAL_DAYS}-day trial work?`,
    a: `Start Pro and use everything for ${PRO_TRIAL_DAYS} days. Cancel before the trial ends and you won’t be charged.`,
  },
  {
    q: 'What is RiskLo AI?',
    a: `An assistant that knows RiskLo and your own data. Ask how your accounts did this month, what’s on the market calendar, how a prop firm’s rules work, or how a number is calculated. It explains facts and never gives trading advice. Pro includes ${AI_MONTHLY_QUESTIONS.pro} questions a month.`,
  },
  {
    q: 'Which brokers can I connect?',
    a: 'NinjaTrader and Tradovate (live and demo), Coinbase, Alpaca, Interactive Brokers, and through SnapTrade: Charles Schwab (incl. thinkorswim), Robinhood, Webull, Fidelity, E*TRADE, Vanguard and many more. Every connection is read-only. Other platforms can import a CSV.',
  },
  {
    q: 'How is billing handled?',
    a: 'Pro is billed through Whop, which handles cards, receipts and cancellations. Manage your subscription from your Account page.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. Cancel from your Account page at any time. Pro stays on until the end of the billing period.',
  },
];

function Cell({ value }) {
  if (value === true) return <IconCheck size={17} className="marketing-compare-icon-yes" aria-label="Included" />;
  if (value === false) return <span className="mp-dash" aria-label="Not included">—</span>;
  return <span className="mp-text">{value}</span>;
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`marketing-faq-item ${open ? 'is-open' : ''}`}>
      <button type="button" className="marketing-faq-q" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span>{q}</span>
        <IconPlus size={18} />
      </button>
      <div className="marketing-faq-a">
        <p>{a}</p>
      </div>
    </div>
  );
}

/** One plan card: price, button, then what's included by area. */
function PlanCard({ plan, index, price, onCta, ctaLabel, busy }) {
  const col = COLUMN[plan.id];
  return (
    <div className={`marketing-plan mp-plan ${plan.featured ? 'featured' : ''} marketing-reveal`} data-delay={index + 1}>
      {plan.featured ? <span className="marketing-plan-badge">Most popular</span> : null}
      <span className="marketing-plan-name">{plan.name}</span>
      <p className="mp-who">{plan.who}</p>
      <div className="marketing-plan-price">
        {price.prefix ? <small className="mp-from">{price.prefix}</small> : null}
        {price.amount}
        {price.period ? <small>{price.period}</small> : null}
      </div>
      <p className="mp-price-note">{price.note || plan.note || ' '}</p>
      <button
        type="button"
        className={`${plan.featured ? 'marketing-btn' : 'marketing-btn-ghost'} mp-cta`}
        onClick={onCta}
        disabled={busy}
      >
        {ctaLabel}
      </button>
      <div className="mp-areas">
        {AREAS.map((area) => {
          const shown = area.rows.filter((r) => r[4]);
          return (
            <div key={area.title} className="mp-area">
              <h4>
                {area.title}
                {area.isNew ? <span className="mp-new">New</span> : null}
              </h4>
              <ul>
                {shown.map((r) => {
                  const v = r[col];
                  return (
                    <li key={r[0]} className={v === false ? 'is-off' : ''}>
                      {v === false
                        ? <span className="mp-dash" aria-hidden="true">—</span>
                        : <IconCheck size={15} aria-hidden="true" />}
                      <span>
                        {typeof v === 'string' ? <strong>{v} · </strong> : null}
                        {r[0]}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PricingPage({ onNavigate, user }) {
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const isSignedIn = Boolean(user?.email);
  const pro = proPriceFor();

  const go = (target) => () => {
    if (onNavigate) onNavigate(target);
  };

  const openWhopCheckout = useCallback(async () => {
    if (!user?.email) {
      if (onNavigate) onNavigate('login');
      return;
    }
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const healthCheck = await fetch(API_ENDPOINTS.health);
      if (!healthCheck.ok) {
        throw new Error('RiskLo is temporarily unavailable. Please try again in a moment.');
      }
      const response = await fetch(API_ENDPOINTS.subscriptionCreateCheckout, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getBearerAuthHeaders(),
        },
        body: JSON.stringify({
          email: user?.email || '',
          useVectorCoupon: false,
        }),
      });
      const contentType = response.headers.get('content-type');
      const data =
        contentType && contentType.includes('application/json')
          ? await response.json()
          : null;
      if (!response.ok) {
        throw new Error(data?.error || data?.message || 'Could not start checkout');
      }
      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      throw new Error('No checkout URL received');
    } catch (e) {
      console.error('Whop checkout:', e);
      setCheckoutError(e.message || 'Could not open checkout');
      setCheckoutLoading(false);
    }
  }, [user?.email, onNavigate]);

  const priceFor = (plan) => {
    if (plan.id === 'basic') return { amount: '$0', period: '/month' };
    if (plan.id === 'company') return { prefix: plan.pricePrefix, amount: plan.price, period: plan.period };
    return {
      amount: pro.label,
      period: '/month',
      note: pro.partner
        ? `Member price · $${PRO_PRICE}/month on your own`
        : `$${PRO_PARTNER_PRICE}/month through a partner like Vector`,
    };
  };

  const ctaFor = (plan) => {
    if (plan.id === 'pro') {
      return {
        onCta: openWhopCheckout,
        label: checkoutLoading ? 'Opening checkout…' : isSignedIn ? 'Upgrade to Pro' : plan.cta,
        busy: checkoutLoading,
      };
    }
    if (plan.id === 'company') return { onCta: go('companies'), label: plan.cta };
    return { onCta: go(isSignedIn ? 'results' : 'login'), label: isSignedIn ? 'Open dashboard' : plan.cta };
  };

  return (
    <>
      <section className="marketing-page-header">
        <div className="marketing-container marketing-container-narrow">
          <div className="marketing-eyebrow marketing-reveal">Pricing</div>
          <h1 className="marketing-title marketing-reveal" data-delay="1">
            Free to start. <span className="accent">Pro when your account is on the line.</span>
          </h1>
          <p className="marketing-subtitle marketing-reveal" data-delay="2" style={{ margin: '1rem auto 0' }}>
            Connect your accounts, check every setup against your prop firm’s rules, and ask RiskLo AI about your
            own numbers. {PRO_TRIAL_DAYS} days free, cancel anytime.
          </p>
        </div>
      </section>

      <section className="marketing-section" style={{ paddingTop: 0 }}>
        <div className="marketing-container">
          <div className="marketing-pricing-grid mp-grid">
            {PLANS.map((plan, i) => {
              const cta = ctaFor(plan);
              return (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  index={i}
                  price={priceFor(plan)}
                  onCta={cta.onCta}
                  ctaLabel={cta.label}
                  busy={cta.busy}
                />
              );
            })}
          </div>
          {checkoutError ? (
            <p className="marketing-subtitle mp-error" role="alert">{checkoutError}</p>
          ) : null}
        </div>
      </section>

      <section className="marketing-section tight">
        <div className="marketing-container">
          <div className="marketing-section-heading marketing-reveal">
            <div className="marketing-eyebrow">Compare plans</div>
            <h2 className="marketing-title" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)' }}>
              Every feature, side by side.
            </h2>
          </div>

          <div className="mp-table-wrap marketing-reveal">
            <table className="marketing-compare-table mp-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th className="center">Basic<span>$0</span></th>
                  <th className="center is-pro">Pro<span>{pro.label}/mo</span></th>
                  <th className="center">Companies<span>from $149/mo</span></th>
                </tr>
              </thead>
              {AREAS.map((area) => (
                <tbody key={area.title}>
                  <tr className="mp-group">
                    <td colSpan={4}>
                      {area.title}
                      {area.isNew ? <span className="mp-new">New</span> : null}
                    </td>
                  </tr>
                  {area.rows.map(([feature, b, p, c]) => (
                    <tr key={feature}>
                      <td className="feature">{feature}</td>
                      <td className="center"><Cell value={b} /></td>
                      <td className="center is-pro"><Cell value={p} /></td>
                      <td className="center"><Cell value={c} /></td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        </div>
      </section>

      <section className="marketing-section tight">
        <div className="marketing-container">
          <div className="marketing-section-heading marketing-reveal">
            <div className="marketing-eyebrow"><IconShield size={14} /> Billing & support</div>
            <h2 className="marketing-title" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)' }}>
              Pricing questions, answered.
            </h2>
          </div>

          <div className="marketing-faq">
            {PRICING_FAQ.map((item) => (
              <FaqItem key={item.q} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="marketing-container">
          <div className="marketing-cta marketing-reveal">
            <div className="marketing-cta-inner">
              <div className="marketing-eyebrow"><IconStar size={14} /> Start free</div>
              <h2>One blown account costs more than a year of Pro.</h2>
              <p>Start on Basic in seconds, then try Pro free for {PRO_TRIAL_DAYS} days.</p>
              <div className="marketing-hero-cta-row" style={{ marginTop: 0 }}>
                <button type="button" className="marketing-btn marketing-btn-lg" onClick={go(isSignedIn ? 'results' : 'login')}>
                  {isSignedIn ? 'Open dashboard' : 'Get started free'}
                </button>
                <button type="button" className="marketing-btn-ghost marketing-btn-lg" onClick={go('features')}>
                  See features
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default PricingPage;
